export type KnopkaGlassPreset = "subtle" | "medium" | "strong" | "hero";
export type KnopkaGlassPerformance = "auto" | "high" | "balanced" | "low";

export type KnopkaGlassLayerOverrides = {
  fillOpacity?: number;
  blur?: number;
  saturate?: number;
  borderOpacity?: number;
  shadowStrength?: number;
  innerGlow?: number;
  noise?: number;
  liquid?: number;
};

export type KnopkaGlassSettings = {
  preset: KnopkaGlassPreset;
  performance: KnopkaGlassPerformance;
  layers?: KnopkaGlassLayerOverrides;
};

export type ResolvedGlassLayers = {
  fillOpacity: number;
  blur: number;
  saturate: number;
  borderOpacity: number;
  shadowStrength: number;
  innerGlow: number;
  noise: number;
  liquid: number;
};

const PRESETS: Record<KnopkaGlassPreset, ResolvedGlassLayers> = {
  subtle: {
    fillOpacity: 5,
    blur: 8,
    saturate: 1.2,
    borderOpacity: 16,
    shadowStrength: 35,
    innerGlow: 30,
    noise: 0,
    liquid: 0,
  },
  medium: {
    fillOpacity: 8,
    blur: 14,
    saturate: 1.35,
    borderOpacity: 22,
    shadowStrength: 50,
    innerGlow: 45,
    noise: 3,
    liquid: 0,
  },
  strong: {
    fillOpacity: 12,
    blur: 18,
    saturate: 1.4,
    borderOpacity: 28,
    shadowStrength: 60,
    innerGlow: 50,
    noise: 4,
    liquid: 0,
  },
  hero: {
    fillOpacity: 10,
    blur: 20,
    saturate: 1.45,
    borderOpacity: 32,
    shadowStrength: 65,
    innerGlow: 55,
    noise: 5,
    liquid: 35,
  },
};

const GLASS_PRESETS = new Set<KnopkaGlassPreset>(["subtle", "medium", "strong", "hero"]);
const GLASS_PERFORMANCE = new Set<KnopkaGlassPerformance>(["auto", "high", "balanced", "low"]);

export const GLASS_CSS_VAR_NAMES = [
  "--knopka-glass-fill",
  "--knopka-glass-fill-dark",
  "--knopka-glass-blur",
  "--knopka-glass-saturate",
  "--knopka-glass-border",
  "--knopka-glass-border-dark",
  "--knopka-glass-shadow",
  "--knopka-glass-inset-top",
  "--knopka-glass-inset-bottom",
  "--knopka-glass-noise-opacity",
  "--knopka-glass-liquid-intensity",
  "--knopka-glass-fill-fallback",
  "--knopka-glass-fill-fallback-dark",
] as const;

export function defaultGlassSettings(): KnopkaGlassSettings {
  return { preset: "medium", performance: "auto" };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function clampLayerOverrides(layers: KnopkaGlassLayerOverrides | undefined): KnopkaGlassLayerOverrides | undefined {
  if (!layers) return undefined;
  const next: KnopkaGlassLayerOverrides = {};
  if (layers.fillOpacity != null) next.fillOpacity = clamp(Number(layers.fillOpacity) || 0, 0, 100);
  if (layers.blur != null) next.blur = clamp(Number(layers.blur) || 0, 0, 40);
  if (layers.saturate != null) next.saturate = clamp(Number(layers.saturate) || 1, 1, 2);
  if (layers.borderOpacity != null) next.borderOpacity = clamp(Number(layers.borderOpacity) || 0, 0, 100);
  if (layers.shadowStrength != null) next.shadowStrength = clamp(Number(layers.shadowStrength) || 0, 0, 100);
  if (layers.innerGlow != null) next.innerGlow = clamp(Number(layers.innerGlow) || 0, 0, 100);
  if (layers.noise != null) next.noise = clamp(Number(layers.noise) || 0, 0, 100);
  if (layers.liquid != null) next.liquid = clamp(Number(layers.liquid) || 0, 0, 100);
  return Object.keys(next).length ? next : undefined;
}

export function parseGlassSettings(raw: unknown): KnopkaGlassSettings {
  if (!raw || typeof raw !== "object") return defaultGlassSettings();
  const data = raw as Record<string, unknown>;
  const preset = GLASS_PRESETS.has(data.preset as KnopkaGlassPreset)
    ? (data.preset as KnopkaGlassPreset)
    : "medium";
  const performance = GLASS_PERFORMANCE.has(data.performance as KnopkaGlassPerformance)
    ? (data.performance as KnopkaGlassPerformance)
    : "auto";
  const layers = clampLayerOverrides(
    data.layers && typeof data.layers === "object" ? (data.layers as KnopkaGlassLayerOverrides) : undefined
  );
  return { preset, performance, layers };
}

export function resolveGlassLayers(settings: KnopkaGlassSettings | null | undefined): ResolvedGlassLayers {
  const base = { ...PRESETS[settings?.preset ?? "medium"] };
  const overrides = settings?.layers;
  if (!overrides) return base;
  if (overrides.fillOpacity != null) base.fillOpacity = overrides.fillOpacity;
  if (overrides.blur != null) base.blur = overrides.blur;
  if (overrides.saturate != null) base.saturate = overrides.saturate;
  if (overrides.borderOpacity != null) base.borderOpacity = overrides.borderOpacity;
  if (overrides.shadowStrength != null) base.shadowStrength = overrides.shadowStrength;
  if (overrides.innerGlow != null) base.innerGlow = overrides.innerGlow;
  if (overrides.noise != null) base.noise = overrides.noise;
  if (overrides.liquid != null) base.liquid = overrides.liquid;
  return base;
}

export function glassSettingsToCssVars(settings: KnopkaGlassSettings | null | undefined): Record<string, string> {
  const layers = resolveGlassLayers(settings);
  const fill = layers.fillOpacity / 100;
  const fillDark = clamp(fill + 0.35, 0.35, 0.85);
  const border = layers.borderOpacity / 100;
  const shadowAlpha = 0.08 + (layers.shadowStrength / 100) * 0.22;
  const innerGlow = layers.innerGlow / 100;
  const noise = layers.noise / 100;
  const liquid = layers.liquid / 100;

  return {
    "--knopka-glass-fill": `rgba(255, 255, 255, ${fill.toFixed(3)})`,
    "--knopka-glass-fill-dark": `rgba(15, 23, 42, ${fillDark.toFixed(3)})`,
    "--knopka-glass-blur": `${layers.blur}px`,
    "--knopka-glass-saturate": String(layers.saturate),
    "--knopka-glass-border": `rgba(255, 255, 255, ${border.toFixed(3)})`,
    "--knopka-glass-border-dark": `rgba(255, 255, 255, ${(border * 0.75).toFixed(3)})`,
    "--knopka-glass-shadow": `0 8px 32px rgba(0, 0, 0, ${shadowAlpha.toFixed(3)})`,
    "--knopka-glass-inset-top": `inset 0 1px 0 rgba(255, 255, 255, ${innerGlow.toFixed(3)})`,
    "--knopka-glass-inset-bottom": `inset 0 -1px 0 rgba(0, 0, 0, ${(innerGlow * 0.25).toFixed(3)})`,
    "--knopka-glass-noise-opacity": noise.toFixed(3),
    "--knopka-glass-liquid-intensity": liquid.toFixed(3),
    "--knopka-glass-fill-fallback": `rgba(255, 255, 255, ${clamp(fill + 0.62, 0.72, 0.92).toFixed(3)})`,
    "--knopka-glass-fill-fallback-dark": `rgba(15, 23, 42, ${clamp(fillDark + 0.1, 0.55, 0.88).toFixed(3)})`,
  };
}

export function applyGlassCssVars(
  root: HTMLElement,
  settings: KnopkaGlassSettings | null | undefined,
  active: boolean
): void {
  if (!active) {
    clearGlassCssVars(root);
    return;
  }
  const resolved = parseGlassSettings(settings ?? defaultGlassSettings());
  root.dataset.knopkaGlassPreset = resolved.preset;
  root.dataset.knopkaGlassPerformance = resolved.performance;
  const vars = glassSettingsToCssVars(resolved);
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
}

export function clearGlassCssVars(root: HTMLElement): void {
  delete root.dataset.knopkaGlassPreset;
  delete root.dataset.knopkaGlassPerformance;
  for (const name of GLASS_CSS_VAR_NAMES) {
    root.style.removeProperty(name);
  }
}
